import { useEffect, useRef, useState } from 'react';
import { agentDisplay } from '../agents';
import type { ApiClient } from '../api';
import { ApiError } from '../api';
import type { ChatRequest, Me } from '../types';

/**
 * The chat panel (§14.4): "A panel in the Shell for direct questions to
 * any agent. Same agents, same tools, same permission layer. The only
 * difference is that a human triggered the run instead of a schedule."
 *
 * §14.4 puts it in the Shell rather than Slack "because responses are
 * frequently tables and charts, which Slack renders badly", so answers
 * get room and a monospace column rather than being squeezed into a chat
 * bubble.
 *
 * Nothing in this component decides what may be asked or answered. The
 * API refuses at the door and the tool layer narrows what the agent can
 * reach; the panel only shows what came back. Hiding a control here would
 * not be access control, exactly as hiding a tile is not (§2.1.3).
 */

/** Agents chat runs today. Mirrors CHAT_AGENTS in shell/api/src/chat.ts. */
const CHAT_AGENTS = [
  // A.F.R.O.B.O.T first (28 October 2026): the dashboard's numbers.
  'reporting', 'social', 'community', 'affiliate', 'finance', 'platform_health',
  'advertiser', 'creator_relations', 'margin', 'sales', 'bd',
  'lifecycle', 'support',
] as const;

const TERMINAL = new Set(['answered', 'failed', 'refused']);

export function ChatView({ api, me }: { api: ApiClient; me: Me }) {
  const [agent, setAgent] = useState<string>(CHAT_AGENTS[0]);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<ChatRequest[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(new Set<string>());

  const addressable = CHAT_AGENTS.filter(
    (a) => me.agents === 'all' || (Array.isArray(me.agents) && me.agents.includes(a)),
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ requests: ChatRequest[] }>('/api/chat')
      .then((r) => {
        if (!cancelled) setHistory(r.requests);
      })
      .catch(() => {
        /* an empty history is not worth an error banner */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  /**
   * Polls anything still running. A chat run is a full agent run, so it
   * takes as long as one; the panel says so rather than pretending a
   * spinner means seconds.
   */
  useEffect(() => {
    const unfinished = history.filter((h) => !TERMINAL.has(h.status));
    if (unfinished.length === 0) return;
    const timer = setInterval(async () => {
      if (document.hidden) return;
      for (const item of unfinished) {
        if (pending.current.has(item.id)) continue;
        pending.current.add(item.id);
        try {
          const fresh = await api.get<ChatRequest>(`/api/chat/${item.id}`);
          setHistory((prev) => prev.map((h) => (h.id === fresh.id ? fresh : h)));
        } catch {
          /* keep the last good state and retry next tick */
        } finally {
          pending.current.delete(item.id);
        }
      }
    }, 3_000);
    return () => clearInterval(timer);
  }, [api, history]);

  const ask = async () => {
    const text = question.trim();
    if (text.length < 3 || sending) return;
    setSending(true);
    setError(null);
    try {
      const created = await api.post<ChatRequest>('/api/chat', { agent, question: text });
      setHistory((prev) => [{ ...created, question: text, agent }, ...prev]);
      setQuestion('');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Your role is not scoped to that agent.'
          : 'That question could not be sent. Nothing was started.',
      );
    } finally {
      setSending(false);
    }
  };

  if (addressable.length === 0) {
    return (
      <div className="card pending-card" style={{ marginTop: 14 }}>
        <h2>Chat</h2>
        <p>Your role is not scoped to any agent that answers questions.</p>
      </div>
    );
  }

  return (
    <div className="chat-panel">
      <div className="card">
        <h2>Ask an agent</h2>
        <p className="muted">
          The agent answers with the tools it always has. Anything it wants to act on still goes to
          the approval queue, and asking here is not approving it.
        </p>

        <div className="chat-agent-picker" role="radiogroup" aria-label="Agent">
          {addressable.map((a) => {
            const display = agentDisplay(a);
            return (
              <button
                key={a}
                role="radio"
                aria-checked={agent === a}
                className="chat-agent"
                data-selected={agent === a}
                style={{ ['--agent-accent' as string]: display.color }}
                onClick={() => setAgent(a)}
              >
                <span aria-hidden="true">{display.icon}</span> {display.displayName}
              </button>
            );
          })}
        </div>

        <textarea
          className="chat-input"
          value={question}
          rows={3}
          placeholder="Which posts are queued for approval this week?"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, shift+Enter is a newline: the queue is
            // keyboard-first (§14.2) and this panel sits beside it.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void ask();
            }
          }}
        />
        <div className="chat-actions">
          <button className="primary" disabled={sending || question.trim().length < 3} onClick={() => void ask()}>
            {sending ? 'Sending' : 'Ask'}
          </button>
          {error && <span className="chat-error">{error}</span>}
        </div>
      </div>

      {history.map((item) => (
        <ChatAnswer key={item.id} item={item} />
      ))}
    </div>
  );
}

function ChatAnswer({ item }: { item: ChatRequest }) {
  const display = agentDisplay(item.agent);
  return (
    <div className="card chat-turn" style={{ ['--agent-accent' as string]: display.color }}>
      <p className="chat-question">{item.question}</p>
      <div className="chat-agent-line">
        <span aria-hidden="true">{display.icon}</span> <strong>{display.displayName}</strong>
      </div>
      {item.status === 'answered' && <pre className="chat-answer">{item.answer}</pre>}
      {item.status === 'refused' && <p className="chat-refused">{item.error}</p>}
      {item.status === 'failed' && (
        <p className="chat-refused">{item.error ?? 'The run did not complete. Nothing was published.'}</p>
      )}
      {!TERMINAL.has(item.status) && (
        <p className="muted chat-waiting">
          Running. This is a full agent run, so it takes about as long as any other.
        </p>
      )}
    </div>
  );
}
